<template>
  <div class="modal fade" role="dialog" id="shopifyAdjustTagsModal">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ t('features.vue.shopify_adjust_tags_modal.title') }}</h4>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <label for="shopify-adjust-tags-selected" class="form-check">
            <input class="form-check-input" id="shopify-adjust-tags-selected" type="radio" value="selectedOrders"
              v-model="selectedOption" />
            {{ t('features.vue.bulk_action_modal.selected_orders', { count: selectedIds.length }) }}
          </label>
          <label for="shopify-adjust-tags-filtered" class="form-check">
            <input class="form-check-input" id="shopify-adjust-tags-filtered" type="radio" value="filteredOrders"
              v-model="selectedOption" />
            <div class="mb-2">{{ t('features.vue.bulk_action_modal.filtered_orders', { count: totalResultsDisplay }) }}</div>
            <input type="number" :max="totalResults" class="form-control" v-model="filteredOrderCount"
              @focus="selectedOption = 'filteredOrders'" />
          </label>

          <div v-if="selectedOption == 'filteredOrders'" class="alert alert-warning mt-3 mb-3">
            {{ t('features.vue.bulk_action_modal.first_results_warning') }}
          </div>

          <hr class="my-4" />

          <div class="d-flex flex-column">
            <label>{{ t('features.vue.adjust_orders_modal.add_tags') }}</label>
            <multiselect v-model="tagsToAdd" :options="allTags" :multiple="true" :show-labels="false"
              :taggable="true" @tag="addTag(tagsToAdd, $event)" />
          </div>

          <div class="d-flex flex-column mt-3">
            <label>{{ t('features.vue.adjust_orders_modal.remove_tags') }}</label>
            <multiselect v-model="tagsToRemove" :options="allTags" :multiple="true" :show-labels="false"
              :taggable="true" @tag="addTag(tagsToRemove, $event)" />
          </div>

          <hr class="my-4" />

          <h5>{{ t('features.vue.adjust_orders_modal.hold_orders') }}</h5>
          <div class="mt-2 d-flex flex-column">
            <label>{{ t('features.vue.adjust_orders_modal.reason') }}</label>
            <multiselect v-model="selectedReason" :options="holdReasons" :multiple="false" :show-labels="false"
              :placeholder="t('features.modals.order_on_hold.select_reason')" />
          </div>
          <div class="mt-3 alert alert-warning">
            {{ t('features.vue.shopify_adjust_tags_modal.hold_warning') }}
          </div>

          <div v-if="error" class="alert alert-danger mt-3 mb-0">
            {{ t('features.vue.shopify_adjust_tags_modal.error') }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary mt-2 mb-2" :disabled="loading || !canSubmit" @click="submit">
            {{ loading ? t('features.vue.common.working') : t('features.vue.shopify_adjust_tags_modal.title') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
  inject: ['i18n'],

  components: { Multiselect },

  props: {
    initialData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      loading: false,
      error: false,
      selectedOption: 'selectedOrders',
      filteredOrderCount: 0,
      selectedIds: [],
      tagsToAdd: [],
      tagsToRemove: [],
      newTags: [],
      selectedReason: null
    }
  },

  computed: {
    filters() {
      return this.initialData.filters || {}
    },

    totalResults() {
      return this.initialData.totalResults || 0
    },

    allTags() {
      return (this.initialData.currentTags || []).concat(this.newTags)
    },

    holdReasons() {
      return this.initialData.holdReasons || []
    },

    selectedCount() {
      return this.selectedOption === 'selectedOrders' ? this.selectedIds.length : Number(this.filteredOrderCount)
    },

    hasChanges() {
      return this.tagsToAdd.length > 0 || this.tagsToRemove.length > 0 || Boolean(this.selectedReason)
    },

    canSubmit() {
      return this.selectedCount > 0 && this.hasChanges
    },

    totalResultsDisplay() {
      return this.totalResults > 1000 ? `up to 1000/${this.totalResults}` : `up to ${this.totalResults}`
    }
  },

  mounted() {
    // Selection lives in sessionStorage (managed by shopify-order-select.js). Read
    // it each time the modal opens so the "selected orders" count is current.
    $('#shopifyAdjustTagsModal').on('show.bs.modal', () => this.refreshSelected())
  },

  methods: {
    t(key, options) {
      return this.i18n.t(key, options)
    },

    refreshSelected() {
      try {
        this.selectedIds = JSON.parse(sessionStorage.shopifyOrders || '[]')
      } catch (e) {
        this.selectedIds = []
      }
    },

    addTag(list, tag) {
      list.push(tag)
      this.newTags.push(tag)
    },

    buildActions() {
      const actions = []

      if (this.tagsToAdd.length || this.tagsToRemove.length) {
        actions.push({ type: 'adjust_tags', payload: { add: this.tagsToAdd, remove: this.tagsToRemove } })
      }

      if (this.selectedReason) {
        actions.push({ type: 'onhold', payload: { reason: this.selectedReason } })
      }

      return actions
    },

    submit() {
      const query = this.selectedOption === 'selectedOrders'
        ? { order_ids: this.selectedIds }
        : { ...this.filters, limit: Number(this.filteredOrderCount) }

      this.loading = true
      this.error = false

      fetch('/shopify/orders/bulk_update', {
        method: 'PUT',
        body: JSON.stringify({
          actions: this.buildActions(),
          ...query
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content
        }
      }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      }).then((data) => {
        const tab = window.open(`/bulk_actions/${data.bulk_action_ids}`, '_blank')
        if (tab) tab.focus()

        $('#shopifyAdjustTagsModal').modal('hide')
      }).catch(() => {
        this.error = true
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>
